// Analytics functionality stub (Firebase removed - TODO: implement Supabase analytics)
import { User, UserRole } from '@/types';

// Stub Analytics type
type Analytics = any;

// Analytics event names - following GA4 conventions
export const ANALYTICS_EVENTS = {
  // Authentication events
  LOGIN: 'login',
  SIGN_UP: 'sign_up',
  LOGOUT: 'logout',
  
  // Page views
  PAGE_VIEW: 'page_view',
  
  // Agent events
  AGENT_VIEW: 'agent_view',
  AGENT_CREATE: 'agent_create',
  AGENT_EDIT: 'agent_edit',
  AGENT_DELETE: 'agent_delete',
  AGENT_PURCHASE: 'agent_purchase',
  AGENT_DOWNLOAD: 'agent_download',
  
  // Platform events
  PLATFORM_ACCESS: 'platform_access',
  WORKFLOW_CREATE: 'workflow_create',
  
  // User journey events
  ROLE_UPGRADE: 'role_upgrade',
  SUBSCRIPTION_START: 'subscription_start',
  PAYMENT_SUCCESS: 'payment_success',
  PAYMENT_FAILED: 'payment_failed',
  
  // Engagement events
  SEARCH: 'search',
  FILTER_APPLY: 'filter_apply',
  SHARE: 'share',
  REVIEW_SUBMIT: 'review_submit',
  
  // System events
  ERROR_ENCOUNTERED: 'error_encountered',
  FEATURE_USED: 'feature_used',
  TUTORIAL_START: 'tutorial_start',
  TUTORIAL_COMPLETE: 'tutorial_complete'
} as const;

class AnalyticsService {
  private analytics: Analytics = null;
  private isEnabled: boolean = false; // Disabled until Supabase analytics is implemented

  constructor() {
    // TODO: Implement Supabase analytics
    console.log('[Analytics] Stub - Firebase analytics removed, awaiting Supabase implementation');
  }

  // Core tracking methods (stubbed)
  trackEvent(eventName: string, parameters?: Record<string, any>) {
    // No-op stub
    if (import.meta.env.DEV) {
      console.log('[Analytics]', eventName, parameters);
    }
  }

  trackPageView(pagePath: string, pageTitle?: string) {
    this.trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
      page_path: pagePath,
      page_title: pageTitle || document.title
    });
  }

  // User identification and properties (stubbed)
  identifyUser(user: User) {
    // No-op stub
    if (import.meta.env.DEV) {
      console.log('[Analytics] Identify user:', user.id, user.role);
    }
  }

  clearUser() {
    // No-op stub
    if (import.meta.env.DEV) {
      console.log('[Analytics] Clear user');
    }
  }

  // Authentication tracking
  trackLogin(method: 'email' | 'google' | 'github', userRole: UserRole) {
    this.trackEvent(ANALYTICS_EVENTS.LOGIN, {
      method,
      user_role: userRole
    });
  }

  trackSignup(method: 'email' | 'google' | 'github') {
    this.trackEvent(ANALYTICS_EVENTS.SIGN_UP, {
      method
    });
  }

  trackLogout() {
    this.trackEvent(ANALYTICS_EVENTS.LOGOUT);
  }

  // Agent-specific tracking
  trackAgentView(agentId: string, agentName: string, category?: string) {
    this.trackEvent(ANALYTICS_EVENTS.AGENT_VIEW, {
      agent_id: agentId,
      agent_name: agentName,
      category: category || 'unknown'
    });
  }

  trackAgentCreate(agentData: {
    id: string;
    name: string;
    category: string;
    platform: string;
    price?: number;
  }) {
    this.trackEvent(ANALYTICS_EVENTS.AGENT_CREATE, {
      agent_id: agentData.id,
      agent_name: agentData.name,
      category: agentData.category,
      platform: agentData.platform,
      price: agentData.price || 0,
      value: agentData.price || 0 // GA4 standard parameter
    });
  }

  trackAgentPurchase(agentData: {
    id: string;
    name: string;
    price: number;
    seller_id: string;
  }) {
    this.trackEvent(ANALYTICS_EVENTS.AGENT_PURCHASE, {
      agent_id: agentData.id,
      agent_name: agentData.name,
      value: agentData.price,
      currency: 'USD',
      seller_id: agentData.seller_id,
      transaction_id: `purchase_${Date.now()}`
    });
  }

  // Platform access tracking
  trackPlatformAccess(platform: 'workflow-builder', action: string) {
    this.trackEvent(ANALYTICS_EVENTS.PLATFORM_ACCESS, {
      platform,
      action,
      timestamp: new Date().toISOString()
    });
  }

  // Role and subscription tracking
  trackRoleUpgrade(fromRole: UserRole, toRole: UserRole, method: 'purchase' | 'admin') {
    this.trackEvent(ANALYTICS_EVENTS.ROLE_UPGRADE, {
      from_role: fromRole,
      to_role: toRole,
      upgrade_method: method
    });
  }

  trackPayment(status: 'success' | 'failed', amount: number, currency: string = 'INR') {
    const eventName = status === 'success' ? ANALYTICS_EVENTS.PAYMENT_SUCCESS : ANALYTICS_EVENTS.PAYMENT_FAILED;
    this.trackEvent(eventName, {
      value: amount,
      currency,
      payment_method: 'razorpay'
    });
  }

  // Search and engagement tracking
  trackSearch(query: string, filters?: Record<string, any>, resultsCount?: number) {
    this.trackEvent(ANALYTICS_EVENTS.SEARCH, {
      search_term: query,
      filters: JSON.stringify(filters || {}),
      results_count: resultsCount
    });
  }

  trackShare(content_type: string, content_id: string, method?: string) {
    this.trackEvent(ANALYTICS_EVENTS.SHARE, {
      content_type,
      content_id,
      method: method || 'unknown'
    });
  }

  // Error tracking
  trackError(error: Error, context?: string) {
    this.trackEvent(ANALYTICS_EVENTS.ERROR_ENCOUNTERED, {
      error_message: error.message,
      error_stack: error.stack,
      context: context || 'unknown',
      user_agent: navigator.userAgent
    });
  }

  // Feature usage tracking
  trackFeatureUsage(feature: string, action: string, metadata?: Record<string, any>) {
    this.trackEvent(ANALYTICS_EVENTS.FEATURE_USED, {
      feature_name: feature,
      action,
      ...metadata
    });
  }

  // RBAC-specific tracking
  trackPermissionCheck(permission: string, granted: boolean, userRole: UserRole) {
    this.trackEvent('permission_check', {
      permission,
      granted,
      user_role: userRole,
      timestamp: new Date().toISOString()
    });
  }

  trackRouteAccess(route: string, allowed: boolean, userRole: UserRole) {
    this.trackEvent('route_access', {
      route,
      allowed,
      user_role: userRole
    });
  }

  // Admin actions tracking
  trackAdminAction(action: string, targetUserId?: string, details?: Record<string, any>) {
    this.trackEvent('admin_action', {
      action,
      target_user_id: targetUserId,
      details: JSON.stringify(details || {}),
      timestamp: new Date().toISOString()
    });
  }

  // Utility methods
  enableAnalytics() {
    this.isEnabled = true;
    localStorage.removeItem('analytics_disabled');
  }

  disableAnalytics() {
    this.isEnabled = false;
    localStorage.setItem('analytics_disabled', 'true');
    this.clearUser();
  }

  isAnalyticsEnabled(): boolean {
    return this.isEnabled;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

// Convenience functions
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => 
  analyticsService.trackEvent(eventName, parameters);

export const trackPageView = (pagePath: string, pageTitle?: string) => 
  analyticsService.trackPageView(pagePath, pageTitle);

export const identifyUser = (user: User) => analyticsService.identifyUser(user);

export default analyticsService;
